import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { reduce, isNull, toUpper, isEmpty, isUndefined, forEach } from 'lodash';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Grid } from '@material-ui/core';
import { checkIsAdult } from '../../../../helpers/dates';
import usePrevious from '../../../../hooks/usePrevious';
import Loader from '../../../../components/Loaders/Loader/Loader';
import TextInputFieldWithValidation from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import DateInputFieldWithValidation from '../../../../components/ValidationFormElements/DateInputFieldWithValidation';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import ArrowIosIcon from '../../../../components/ClaimFormComponents/ArrowIosIcon';
import CustomErrorMessage from '../../../../components/ClaimFormComponents/CustomErrorMessage';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import SubmitFormWrapper from '../../../../components/FormComponents/SubmitFormWrapper';
import searchApplicantValidationSchema from '../../../../validations/claimValidationSchema/searchApplicantSchema';
import InputAttentionMessage from '../../../../components/ClaimFormComponents/InputAttentionMessage';

import useStyles from '../../useStyles';
import styles from '../steps.module.scss';

const SearchApplicantStep = ({
  goNext,
  blockNextSteps,
  step,
  setStepNavigationBox,
  dataForSearch,
  setDataForSearch,
  isLoading,
  applicant,
  params,
  searchForApplicant,
  setApplicantIsAdult,
  updateForm,
  customError,
}) => {
  const { type: claimType } = params;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const isDeviceLgSize = useDeviceResize('lg');
  const isDeviceMdSize = useDeviceResize('md');
  const {
    register,
    setValue,
    errors,
    triggerValidation,
    clearError,
    handleSubmit,
    setError,
  } = useForm({
    validationSchema: searchApplicantValidationSchema,
    mode: 'onBlur',
  });

  const customValidationErrors = useMemo(() => {
    if (customError) {
      return reduce(
        customError,
        (acc, v, k) => {
          acc[`${k}`] = v;
          return acc;
        },
        {},
      );
    }
    return {};
  }, [customError]);

  useEffect(() => {
    if (customValidationErrors) {
      forEach(customValidationErrors, (value, key) => {
        setError(key, 'backendValidation', t(value[0]));
      });
    }
  }, [customValidationErrors, setError, t]);

  const previousIsSearching = usePrevious(isLoading);

  useEffect(() => {
    register({ name: 'pnum' });
    register({ name: 'bdate' });
    register({ name: 'ssn' });
    register({ name: 'policy' });
  }, [register]);

  useEffect(() => {
    if (!isNull(applicant) && !isUndefined(applicant)) {
      setDataForSearch((prevState) => ({
        policy: applicant.policyNumber ?? prevState.policy,
        pnum: applicant.passportNum ?? prevState.pnum,
        bdate: applicant.birthday ?? prevState.bdate,
        ssn: applicant.socialCard ?? prevState.ssn,
      }));
    }
  }, [applicant, setDataForSearch]);

  useEffect(() => {
    if (!isNull(applicant) && !isUndefined(applicant)) {
      setValue('pnum', applicant.passportNum, !!applicant.passportNum);
      setValue(
        'bdate',
        applicant.birthday,
        !isNull(applicant.birthday) && !!applicant.birthday.length,
      );
      setValue('ssn', applicant.socialCard, !!applicant.socialCard.length);
      setValue('policy', applicant.policyNumber, !!applicant.policyNumber.length);
    }
  }, [dataForSearch, setValue, applicant]);

  useEffect(() => {
    if (!isEmpty(errors)) {
      blockNextSteps();
    }
  }, [errors, blockNextSteps]);

  useEffect(() => {
    if (
      !isNull(applicant)
            && previousIsSearching === true
            && !isLoading
            && (isNull(customError) || isUndefined(customError))
    ) {
      goNext();
    }
  }, [goNext, isLoading, previousIsSearching, applicant, customError]);

  const handleInputChange = useCallback(
    (value, name) => {
      const targetVal = !isNull(value) ? toUpper(value) : null;
      setDataForSearch({
        ...dataForSearch,
        [name]: targetVal,
      });
      setValue(name, targetVal, true);
    },
    [dataForSearch, setDataForSearch, setValue],
  );

  const handleSearch = useCallback(
    (data) => {
      blockNextSteps(step.n);
      dispatch(
        updateForm({
          name: 'personalData',
          data: {
            birthday: data.bdate,
          },
        }),
      );
      dispatch(searchForApplicant({ ...data }));
      dispatch(setApplicantIsAdult(checkIsAdult(data.bdate)));
    },
    [blockNextSteps, dispatch, searchForApplicant, setApplicantIsAdult, step.n, updateForm],
  );

  const onSubmit = useCallback(
    (data) => {
      const preparedData = {
        ...data,
        bdate: format(data.bdate, 'yyyy-MM-dd'),
        claimType,
      };

      if (preparedData.policy) {
        delete preparedData.ssn;
      } else {
        delete preparedData.policy;
      }
      if (!isNull(applicant)) {
        goNext();
      } else {
        handleSearch(preparedData);
      }
      clearError();
    },
    [claimType, clearError, handleSearch, applicant, goNext],
  );

  return (
    <SubmitFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formContainer}>
        {isLoading && (
          <div>
            <Loader />
          </div>
        )}
        <Grid container spacing={4}>
          <Grid item md={5} xs={12}>
            <TextInputFieldWithValidation
              required
              formProps={{ errors, clearError }}
              labelKey="searchApplicant.physical.passOrIdNum"
              name="pnum"
              value={dataForSearch.pnum}
              handleChange={({ target }) => handleInputChange(target.value, 'pnum')}
            />
            <InputAttentionMessage message={t('warningMessage.provideValidPassport')} />
          </Grid>
          <Grid item md={2} className={classes.medicalEmptyGridItem} />
          <Grid item md={5} xs={12}>
            <DateInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="birthDatePlaceholder"
              name="bdate"
              value={dataForSearch.bdate}
              handleChange={(date) => {
                // eslint-disable-next-line no-restricted-globals
                if (date instanceof Date && !isNaN(date)) {
                  const formattedDate = format(date, 'yyyy-MM-dd');
                  handleInputChange(formattedDate, 'bdate');
                } else {
                  handleInputChange(null, 'bdate');
                }
              }}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.divider} />
          </Grid>
          <Grid item md={5} xs={12}>
            <TextInputFieldWithValidation
              required
              formProps={{ errors, clearError }}
              labelKey="searchApplicant.physical.ssn"
              name="ssn"
              value={dataForSearch.ssn}
              handleChange={({ target }) => {
                handleInputChange(target.value, 'ssn');
                triggerValidation({ name: 'policy' });
              }}
            />
          </Grid>
          <ArrowIosIcon isDeviceMdSize={isDeviceMdSize} />
          <Grid item md={5} xs={12}>
            <TextInputFieldWithValidation
              required
              formProps={{ errors, clearError }}
              labelKey="searchApplicant.physical.policy"
              name="policy"
              value={dataForSearch.policy}
              handleChange={({ target }) => {
                handleInputChange(target.value, 'policy');
              }}
            />
          </Grid>

          {customError && (
            <Grid item xs={12}>
              <CustomErrorMessage
                customErrorCode={t(`backendErrors.${customError.code}`, customError.errorData)}
              />
            </Grid>
          )}
        </Grid>
        <ClaimStepNavigationWrapper
          clearError={clearError}
          errors={errors}
          isNextDisabled={isLoading}
          handleSubmit={handleSubmit}
          setStepNavigationBox={setStepNavigationBox}
          isDeviceLgSize={isDeviceLgSize}
          onSubmit={onSubmit}
          dependencies={[JSON.stringify(errors), isDeviceLgSize, isLoading]}
          styles={classes.searchNavigationButton}
        />
      </div>
    </SubmitFormWrapper>

  );
};

SearchApplicantStep.propTypes = {
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  step: PropTypes.shape({
    claimForm: PropTypes.string.isRequired,
    n: PropTypes.number.isRequired,
  }).isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
  dataForSearch: PropTypes.shape({
    policy: PropTypes.string,
    pnum: PropTypes.string,
    bdate: PropTypes.string,
    ssn: PropTypes.string,
  }).isRequired,
  setDataForSearch: PropTypes.func.isRequired,
  params: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  customError: PropTypes.shape({
    code: PropTypes.string,
    errorData: PropTypes.shape({}),
  }),
  applicant: PropTypes.shape({
    policyNumber: PropTypes.string,
    passportNum: PropTypes.string,
    birthday: PropTypes.string,
    socialCard: PropTypes.string,
  }),
  // title: PropTypes.string.isRequired, // In future need to add condition to show {title}
  searchForApplicant: PropTypes.func.isRequired,
  setApplicantIsAdult: PropTypes.func.isRequired,
  updateForm: PropTypes.func.isRequired,
};

SearchApplicantStep.defaultProps = {
  customError: null,
  applicant: null,
};

export default SearchApplicantStep;
